import { toast } from '@music-vine/cadence/ui';
import { captureException } from '@sentry/nextjs';
import clsx from 'clsx';
import type Hls from 'hls.js';
import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import trackPlay from '@/components/MGTile/api/trackPlay';
import { playbackControlAtom } from '@/features/AudioPlayer/atoms/playback/playbackAtoms';
import useSlugEnd from '@/hooks/navigation/useSlugEnd';
import { useVideoKeyInputs } from '@/hooks/media/useVideoKeyInputs';
import { useVideoMouseRest } from '@/hooks/media/useVideoMouseRest';
import { logEvent } from '@/pages/api/v1/utils';
import { ASSET_TYPE, type AssetType } from '@/types/asset';
import { trackInternalEvent } from '@/utils/tracking';
import { cn } from '@/lib/utils';
import FullscreenButton from './FullscreenButton';
import PlayButton from './PlayButton';
import SeekSlider from './SeekSlider';
import Time from './Time';
import { getVideoPlayerVolume } from './utils/getVideoPlayerVolume';
import { saveVideoPlayerVolume } from './utils/saveVideoPlayerVolume';
import VolumeSlider from './VolumeSlider';
interface VideoPlayerHLSProps {
  assetId: string;
  assetType?: AssetType;
  autoPlay?: boolean;
  className?: string;
  containerClassName?: string;
  forceMuted?: boolean;
  hasAudio?: boolean;
  loop?: boolean;
  muted?: boolean;
  poster?: string;
  src: string;
}
const VideoPlayerHLS = ({
  src,
  autoPlay = true,
  poster,
  muted = true,
  forceMuted = false,
  hasAudio = false,
  loop = false,
  assetId,
  assetType = ASSET_TYPE.MOTION,
  className,
  containerClassName
}: VideoPlayerHLSProps) => {
  const volumeSettingsCallback = useCallback(() => {
    if (forceMuted) {
      return {
        volume: 0,
        muted: true
      };
    }
    const videoPlayerVolume = getVideoPlayerVolume();
    if (videoPlayerVolume) {
      return videoPlayerVolume;
    }
    return {
      volume: muted ? 0 : 1,
      muted
    };
  }, [muted, forceMuted]);
  const volumeSettings = volumeSettingsCallback();
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMuted, setIsMuted] = useState(volumeSettings.muted);
  const [volume, setVolume] = useState(volumeSettings.volume);
  const {
    asPath
  } = useRouter();
  const slugEnd = useSlugEnd();
  const hasTrackedPlay = useRef(false);
  const prevAssetId = useRef(assetId);
  if (prevAssetId.current !== assetId) {
    prevAssetId.current = assetId;
    hasTrackedPlay.current = false;
  }
  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    shouldShowUI,
    handlers
  } = useVideoMouseRest({
    isPlaying,
    enabled: true
  });
  const setPlaybackControl = useSetAtom(playbackControlAtom);
  const pauseAudioPlayer = useCallback(() => {
    setPlaybackControl('pause');
  }, [setPlaybackControl]);
  const onPlay = useCallback(async () => {
    try {
      await videoPlayerRef.current?.play();
      setIsPlaying(true);
      if (hasAudio && !isMuted) {
        pauseAudioPlayer();
      }
    } catch {
      // Video play can fail due to browser autoplay policies — safe to ignore
    }
  }, [hasAudio, isMuted, pauseAudioPlayer]);
  const onPause = useCallback(() => {
    try {
      videoPlayerRef.current?.pause();
      setIsPlaying(false);
    } catch {
      // Safe to ignore — pause failures are non-critical
    }
  }, []);
  const onVolumeChange = useCallback((value: number[]) => {
    if (value.length) {
      const [currentValue] = value;
      const videoPlayer = videoPlayerRef.current;
      if (!videoPlayer) {
        return;
      }
      if (currentValue === 0) {
        videoPlayer.volume = currentValue;
        videoPlayer.muted = true;
        setIsMuted(true);
        saveVideoPlayerVolume({
          volume: currentValue,
          muted: true
        });
        return;
      }
      videoPlayer.volume = currentValue;
      videoPlayer.muted = false;
      setVolume(currentValue);
      setIsMuted(false);
      saveVideoPlayerVolume({
        volume: currentValue,
        muted: false
      });
      if (hasAudio && isPlaying) {
        pauseAudioPlayer();
      }
    }
  }, [hasAudio, isPlaying, pauseAudioPlayer]);
  const onSeekChange = useCallback((value: number[]) => {
    if (value.length) {
      const [currentValue] = value;
      if (videoPlayerRef.current) {
        videoPlayerRef.current.currentTime = currentValue;
        setCurrentTime(currentValue);
      }
    }
  }, []);
  const updateTime = useCallback(() => {
    if (videoPlayerRef.current) {
      setCurrentTime(videoPlayerRef.current.currentTime);
    }
    animationFrameRef.current = requestAnimationFrame(updateTime);
  }, []);
  useEffect(() => {
    const videoPlayer = videoPlayerRef.current;
    const autoPlayVideo = () => {
      if (autoPlay) {
        void videoPlayer?.play();
      }
    };

    // hls.js is 152KB gz, so it is only fetched once a player actually mounts
    // rather than at pageload.
    let isCancelled = false;
    let hlsInstance: Hls | null = null;
    let hasNativeListener = false;

    // Used when hls.js is unavailable — either the browser can't use MSE, or the
    // async chunk failed to load.
    const setUpNativePlayback = () => {
      if (videoPlayer?.canPlayType('application/vnd.apple.mpegurl')) {
        hasNativeListener = true;
        videoPlayer.src = src;
        videoPlayer.addEventListener('loadedmetadata', autoPlayVideo);
      }
    };
    const setUpPlayback = async () => {
      let HlsPlayer: typeof Hls;
      try {
        HlsPlayer = (await import('hls.js')).default;
      } catch (error) {
        captureException(error, {
          extra: {
            videoSrc: src,
            context: 'hls-dynamic-import'
          }
        });
        if (!isCancelled) {
          setUpNativePlayback();
        }
        return;
      }
      if (isCancelled) {
        return;
      }
      if (HlsPlayer.isSupported() && videoPlayer) {
        // ponytail: no startLevel override here — unlike the grid tiles, this
        // player sizes itself from the rendition's intrinsic dimensions, and HLS
        // can't retroactively upgrade buffered segments without a flush + stall.
        const hls = new HlsPlayer();
        hlsInstance = hls;
        hls.loadSource(src);
        hls.attachMedia(videoPlayer);
        hls.on(HlsPlayer.Events.MANIFEST_PARSED, autoPlayVideo);
        return;
      }
      setUpNativePlayback();
    };
    void setUpPlayback();
    return () => {
      isCancelled = true;
      hlsInstance?.destroy();
      if (hasNativeListener) {
        videoPlayer?.removeEventListener('loadedmetadata', autoPlayVideo);
      }
    };
  }, [src, autoPlay]);
  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateTime);
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying, updateTime]);
  useEffect(() => {
    const handleFullscreenChange = () => {
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
      };
      setIsFullscreen(!!(doc.fullscreenElement ?? doc.webkitFullscreenElement));
    };
    const handleScreenSizeChange = (e: MediaQueryListEvent) => {
      if (e.matches && isFullscreen) {
        // If screen becomes small (matches sm breakpoint) and we're in fullscreen, exit fullscreen
        const doc = document as Document & {
          webkitExitFullscreen?: () => Promise<void>;
        };
        if (doc.exitFullscreen) {
          void doc.exitFullscreen();
        } else if (doc.webkitExitFullscreen) {
          void doc.webkitExitFullscreen();
        }
      }
    };
    const mediaQuery = window.matchMedia('(max-width: 640px)');

    // Check initial state
    if (mediaQuery.matches && isFullscreen) {
      handleScreenSizeChange({
        matches: true
      } as MediaQueryListEvent);
    }

    // Add listeners
    mediaQuery.addEventListener('change', handleScreenSizeChange);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      mediaQuery.removeEventListener('change', handleScreenSizeChange);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreen]);
  const toggleFullscreen = useCallback(async () => {
    try {
      const doc = document as Document & {
        webkitFullscreenElement?: Element;
        webkitExitFullscreen?: () => Promise<void>;
      };
      const container = containerRef.current as HTMLElement & {
        webkitRequestFullscreen?: () => Promise<void>;
      };
      if (!(doc.fullscreenElement || doc.webkitFullscreenElement)) {
        if (container?.requestFullscreen) {
          await container.requestFullscreen();
        } else if (container?.webkitRequestFullscreen) {
          await container.webkitRequestFullscreen();
        }
      } else if (doc.exitFullscreen) {
        await doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        await doc.webkitExitFullscreen();
      }
    } catch {
      toast.error('Unable to fullscreen the video at this time');
    }
  }, []);
  const handleVideoClick = (event: React.MouseEvent) => {
    if (event.target === videoPlayerRef.current) {
      if (isPlaying) {
        onPause();
      } else {
        void onPlay();
      }
    }
  };
  const handleVideoDoubleClick = (event: React.MouseEvent) => {
    if (event.target === videoPlayerRef.current) {
      void toggleFullscreen();
    }
  };
  const onToggleMute = useCallback(() => {
    if (!videoPlayerRef.current) {
      return;
    }
    const newMutedState = !isMuted;
    videoPlayerRef.current.muted = newMutedState;
    setIsMuted(newMutedState);
    if (!newMutedState && hasAudio && isPlaying) {
      pauseAudioPlayer();
    }
  }, [isMuted, hasAudio, isPlaying, pauseAudioPlayer]);
  const handlePlayButtonClick = useCallback(() => {
    if (isPlaying) {
      return onPause();
    }
    return onPlay();
  }, [isPlaying, onPause, onPlay]);
  useVideoKeyInputs({
    videoRef: videoPlayerRef,
    currentTime,
    duration,
    isPlaying,
    onPause,
    onPlay,
    setCurrentTime,
    toggleFullscreen,
    onToggleMute
  });
  return <div className={cn('relative flex flex-col items-center justify-center overflow-hidden', isFullscreen ? 'rounded-none' : 'rounded-xl', containerClassName)} ref={containerRef} {...handlers} data-sentry-component="VideoPlayerHLS" data-sentry-source-file="index.tsx">
      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <video className={cn('h-full w-full object-contain', isFullscreen ? '' : 'rounded-xl', className)} data-testid="video-player" loop={loop} muted={isMuted} onClick={handleVideoClick} onContextMenu={event => {
        event.preventDefault();
      }} onDoubleClick={handleVideoDoubleClick} onEnded={() => {
        setCurrentTime(duration);
        setIsPlaying(false);
      }} onLoadedMetadata={() => {
        setDuration(videoPlayerRef.current?.duration ?? 0);
      }} onPause={() => setIsPlaying(false)} onPlay={() => {
        setIsPlaying(true);
        if (!hasTrackedPlay.current) {
          hasTrackedPlay.current = true;
          try {
            void logEvent({
              event_type: 'play',
              asPath,
              metadata: {
                asset_type: assetType,
                asset_id: Number(assetId)
              }
            });
            if (assetType === ASSET_TYPE.MOTION) {
              void trackPlay({
                slug: slugEnd ?? '',
                assetId
              });
            }
            void trackInternalEvent({
              type: 'play'
            });
          } catch {
            // Tracking failures are non-critical
          }
        }
      }} onSeeked={event => setCurrentTime(event.currentTarget.currentTime)} onTimeUpdate={() => {
        if (currentTime >= duration) {
          setCurrentTime(0);
        }
      }} playsInline poster={poster} ref={videoPlayerRef}>
          <source type="application/x-mpegURL" />
          <track kind="captions" label="english_captions" src={undefined} srcLang="en" />
          Sorry, your browser does not support video playback.
        </video>
      </div>
      <div className={clsx('absolute bottom-0 w-full transition-opacity duration-300', shouldShowUI ? 'opacity-100' : 'opacity-0')} onBlur={handlers.onControlsBlur} onFocus={handlers.onControlsFocus} onMouseEnter={handlers.onControlsMouseEnter} onMouseLeave={handlers.onControlsMouseLeave}>
        <div className="fl-gap-2/4 relative flex max-h-12 flex-row items-end bg-black/60 p-2 transition-all focus-within:bg-black/75 focus-within:backdrop-blur-md hover:bg-black/75 hover:backdrop-blur-md">
          <div className="flex w-full flex-row items-center gap-4">
            <PlayButton isPlaying={isPlaying} onClick={handlePlayButtonClick} data-sentry-element="PlayButton" data-sentry-source-file="index.tsx" />
            <Time currentValue={currentTime} maxValue={duration} data-sentry-element="Time" data-sentry-source-file="index.tsx" />
            <SeekSlider max={duration} onSeekChange={onSeekChange} value={currentTime} data-sentry-element="SeekSlider" data-sentry-source-file="index.tsx" />
          </div>
          <div className="flex flex-row items-end gap-0.5 md:gap-1">
            {hasAudio && <VolumeSlider disabled={!hasAudio} muted={isMuted} onVolumeChange={onVolumeChange} orientation="vertical" volume={volume} />}
            <div className="hidden sm:block">
              <FullscreenButton onClick={toggleFullscreen} data-sentry-element="FullscreenButton" data-sentry-source-file="index.tsx" />
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default VideoPlayerHLS;